import { useQuery } from '@tanstack/react-query';
import {
  getProjects,
  getProject,
  getProjectTasks,
  getProjectNotes,
  getClientProjects,
} from '../services/projectsApi';
import type { ProjectFilters } from '../types/project';

// Query keys for caching
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters?: ProjectFilters) => [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  tasks: (projectId: string) => [...projectKeys.all, 'tasks', projectId] as const,
  notes: (projectId: string) => [...projectKeys.all, 'notes', projectId] as const,
  client: (clientId: string) => [...projectKeys.all, 'client', clientId] as const,
};

/**
 * Hook to fetch projects with filters and pagination
 */
export const useProjects = (filters?: ProjectFilters) => {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: () => getProjects(filters),
  });
};

/**
 * Hook to fetch a single project
 */
export const useProject = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => getProject(id),
    enabled: options?.enabled ?? !!id,
  });
};

/**
 * Hook to fetch tasks for a project
 */
export const useProjectTasks = (
  projectId: string,
  filters?: { status?: string },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [...projectKeys.tasks(projectId), filters],
    queryFn: () => getProjectTasks(projectId, filters),
    enabled: options?.enabled ?? !!projectId,
  });
};

/**
 * Hook to fetch notes for a project
 */
export const useProjectNotes = (projectId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: projectKeys.notes(projectId),
    queryFn: () => getProjectNotes(projectId),
    enabled: options?.enabled ?? !!projectId,
  });
};

/**
 * Hook to fetch projects for a specific client
 */
export const useClientProjects = (clientId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: projectKeys.client(clientId),
    queryFn: () => getClientProjects(clientId),
    enabled: options?.enabled ?? !!clientId,
  });
};

export default useProjects;
